import React, {useMemo, useState} from "react";
import {Button, Modal, Typography} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ComponentPropsFromRoute} from "../../../../../routers/routers";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {HealthCheckerGroup} from "../../../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../../../services/health-checker/health-checker.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {SmartTable} from "../../../../common/smart-table/smart-table";
import {SmartTableDataHandler} from "../../../../common/smart-table/smart-table.data-handler";
import {columnsFactory} from "./check-groups.columns";
import {HealthCheckerGroupModal} from "./check-groups.modal";
import "./check-groups.scss";

const {Text, Title} = Typography;

export const HealthCenterCheckGroups: React.FC<ComponentPropsFromRoute> = () => {
    const t = useTranslate();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [editableGroup, setEditableGroup] = useState<HealthCheckerGroup | null>(null);

    const columns = useMemo(() => columnsFactory(t), [t]);
    const handler = useMemo(
        () => new SmartTableDataHandler<HealthCheckerGroup>((request) => healthCheckerService.getGroups(request)),
        []
    );

    const openCreateModal = () => {
        setEditableGroup(null);
        setModalOpen(true);
    };

    const openEditModal = (group: HealthCheckerGroup) => {
        setEditableGroup(group);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditableGroup(null);
    };

    const removeGroup = (group: HealthCheckerGroup) => {
        Modal.confirm({
            title: t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.REMOVE-TITLE", {
                defaultValue: "Delete check group?",
            }),
            content: t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.REMOVE-DESCRIPTION", {
                name: group.name,
                defaultValue: 'The group "{{name}}" will be permanently deleted.',
            }),
            okText: t("REMOVE", {defaultValue: "Delete"}),
            cancelText: t("CANCEL", {defaultValue: "Cancel"}),
            okButtonProps: {danger: true},
            onOk: async () => {
                await healthCheckerService.removeGroup(group.id);
                await handler.reloadItemsWithLastRequest();
                notifications.successfully();
            },
        });
    };

    return (
        <section className="health-center-check-groups">
            <div className="health-center-check-groups__toolbar">
                <div>
                    <Title level={4}>
                        {t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.TITLE", {defaultValue: "Check groups"})}
                    </Title>
                    <Text type="secondary">
                        {t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.DESCRIPTION", {
                            defaultValue: "Configure recipients and notification methods for health checks",
                        })}
                    </Text>
                </div>
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={["fas", "plus"]} />}
                    onClick={openCreateModal}
                >
                    {t("HEALTH-CENTER.SETTINGS.CHECK-GROUPS.CREATE", {defaultValue: "Create group"})}
                </Button>
            </div>

            <SmartTable
                handler={handler}
                columns={columns}
                actions={[
                    {
                        icon: "edit",
                        title: t("EDIT", {defaultValue: "Edit"}),
                        onClick: openEditModal,
                    },
                    {
                        icon: "trash",
                        title: t("REMOVE", {defaultValue: "Delete"}),
                        onClick: removeGroup,
                    },
                ]}
            />

            {modalOpen ? (
                <HealthCheckerGroupModal
                    group={editableGroup}
                    onCancel={closeModal}
                    onComplete={() => handler.reloadItemsWithLastRequest()}
                />
            ) : null}
        </section>
    );
};
